import { evaluateAuthRule, type AuthRule } from "#shared/authRule";
import { mapUserClaims, type UserClaim } from "#shared/mapUserClaims";
import { bffFetch } from "~/utils/bff-fetch";
import { safeInternalPath } from "~/utils/safePath";

export type { AuthRule };

/**
 * Canonical authenticated user record. The full claim set lives on `claims`
 * so frontmatter rules can match on arbitrary IdP-emitted claim names.
 */
export interface AuthUser {
  id: string;
  name?: string;
  email?: string;
  /** Always `oidc` — sessions are owned by the external Duende BFF. */
  provider: string;
  /** Claim map as returned by the BFF, normalised by `mapUserClaims`. */
  claims: Record<string, unknown>;
}

const STATE_KEY = "auth:user";

/**
 * Auth state + access-rule evaluator.
 *
 * Sessions are owned by the Duende BFF (`EConnect.Fabric.Bff`) — the
 * `__Host-bff` cookie is HttpOnly, so the browser only learns the
 * user's stripped claim set via `/bff/auth/user`. This composable
 * mirrors that response into a reactive store so components and route
 * guards can render synchronously after hydration.
 *
 * `signIn()` and `signOut()` are full-page navigations to the BFF —
 * never `fetch()`s — because the IdP redirect flow requires a top-level
 * navigation.
 */
export function useAuth() {
  const user = useState<AuthUser | null>(STATE_KEY, () => null);
  const isAuthenticated = computed(() => !!user.value);

  function signIn(opts?: { redirect?: string }): void {
    if (!import.meta.client) return;
    const target =
      opts?.redirect ?? window.location.pathname + window.location.search;

    // Circuit breaker mirroring `middleware/auth.global.ts`, sharing its
    // `auth:loop:<path>` namespace so the two cooperate. The breaker doesn't
    // cause loops; it catches one already in progress — a BFF misconfig
    // (oversized session cookie, unexpected claim shape, bad `returnUrl`) can
    // bounce the tab through IdP → callback → here forever — and surfaces a
    // readable error instead. `showError` rather than `throw` because this
    // runs in a click handler, not Nuxt middleware. 2s window: a real loop
    // re-fires within milliseconds.
    const path = new URL(target, window.location.origin).pathname;
    const breakerKey = "auth:loop:" + path;
    const last = Number(sessionStorage.getItem(breakerKey) ?? 0);
    const now = Date.now();
    if (now - last < 2_000) {
      sessionStorage.removeItem(breakerKey);
      console.error("[auth] redirect loop detected for", path);
      showError(
        createError({
          statusCode: 500,
          statusMessage: `Auth redirect loop on ${path}. Inspect DevTools → Application → Cookies for __Host-bff size, then the /bff/auth/user response.`,
          fatal: true,
        }),
      );
      return;
    }
    sessionStorage.setItem(breakerKey, String(now));

    window.location.assign(
      `/bff/auth/login?returnUrl=${encodeURIComponent(target)}`,
    );
  }

  function setUser(next: AuthUser | null): void {
    user.value = next;
  }

  async function signOut(
    logoutMessage = "return to EConnect",
    returnUrl = "/",
  ): Promise<void> {
    if (!import.meta.client) return;
    // Same-origin path only — reject absolute/protocol-relative returnUrls so a
    // caller-supplied value can't bounce the post-logout navigation off-site.
    const safeReturnUrl = safeInternalPath(returnUrl, "/");
    const sid = await getSessionId(user.value);

    user.value = null;

    if (!sid) {
      window.location.assign(safeReturnUrl);
      return;
    }

    const url = new URL("/bff/auth/logout", window.location.origin);
    url.searchParams.set("sid", sid);
    url.searchParams.set("returnUrl", safeReturnUrl);
    // Always forward the logout message; the BFF relays it to the IdP as a
    // `logoutMessage` protocol param (OnRedirectToIdentityProviderForSignOut).
    if (logoutMessage) {
      url.searchParams.set("logoutMessage", logoutMessage);
    }
    window.location.assign(url.pathname + url.search);
  }

  // Thin wrapper around the shared evaluator — closes over the reactive
  // session so callers don't need to thread claims through. The grammar
  // and matcher live in `shared/authRule.ts`; both this composable and
  // the Nitro middleware in `server/middleware/auth.ts` delegate there.
  function evaluateRule(rule: AuthRule | undefined): boolean {
    return evaluateAuthRule(
      rule,
      user.value?.claims ?? {},
      isAuthenticated.value,
    );
  }

  return { user, isAuthenticated, signIn, signOut, setUser, evaluateRule };
}

function readSessionId(
  claims: Record<string, unknown> | undefined,
): string | null {
  const sid = claims?.sid;
  if (typeof sid === "string" && sid.length > 0) return sid;
  if (Array.isArray(sid)) {
    const first = sid.find(
      (value) => typeof value === "string" && value.length > 0,
    );
    return typeof first === "string" ? first : null;
  }
  return null;
}

async function fetchSessionId(): Promise<string | null> {
  try {
    const res = await bffFetch("/bff/auth/user", {
      headers: { accept: "application/json" },
    });
    if (res.status !== 200) return null;
    const claims = (await res.json()) as UserClaim[] | null;
    const sid = claims?.find((claim) => claim.type === "sid")?.value;
    return typeof sid === "string" && sid.length > 0 ? sid : null;
  } catch {
    return null;
  }
}

async function getSessionId(
  currentUser: AuthUser | null,
): Promise<string | null> {
  return readSessionId(currentUser?.claims) ?? (await fetchSessionId());
}

/** Pure: claims → AuthUser. Does not read or write reactive state. */
export function claimsToAuthUser(
  claims: Record<string, unknown>,
  overrides?: Partial<AuthUser>,
): AuthUser {
  const sub = typeof claims.sub === "string" ? claims.sub : "";
  const name =
    (typeof claims.name === "string" && claims.name) ||
    (typeof claims.preferred_username === "string" &&
      claims.preferred_username) ||
    undefined;
  const email = typeof claims.email === "string" ? claims.email : undefined;
  return { id: sub, name, email, provider: "oidc", claims, ...overrides };
}

/**
 * Fetch `/bff/auth/user` and apply the result to state. Returns the
 * resulting user, or `null` for any 401/empty response (signed out).
 *
 * Bounded by an abort timeout: the BFF proxies an mTLS round-trip to the IdP,
 * which can hang. This runs on boot (UX priming) and inside the route gate
 * (`middleware/auth.global.ts`); an unbounded fetch would stall a protected-page
 * navigation indefinitely. On timeout the fetch aborts, we fall through the
 * `catch` to `null`, and the caller treats it as signed-out (bounce to sign-in)
 * rather than hanging.
 */
const HYDRATE_TIMEOUT_MS = 4_000;

export async function hydrateAuthFromMe(
  setUser: (u: AuthUser | null) => void,
): Promise<AuthUser | null> {
  try {
    const res = await bffFetch("/bff/auth/user", {
      headers: { accept: "application/json" },
      signal: AbortSignal.timeout(HYDRATE_TIMEOUT_MS),
    });
    if (res.status !== 200) {
      setUser(null);
      return null;
    }
    const payload = (await res.json()) as UserClaim[] | null;
    const claims = mapUserClaims(payload);
    if (!claims) {
      setUser(null);
      return null;
    }
    const user = claimsToAuthUser(claims);
    setUser(user);
    return user;
  } catch {
    // Offline / 5xx — caller decides whether to leave state empty.
    return null;
  }
}
